/**
 * Client-side image downscaling for OCR uploads.
 *
 * Why downscale on the client:
 *  - Gemini has per-request payload limits and per-image cost; sending 12MP
 *    phone photos burns money and time for no recognition benefit.
 *  - Sending raw images leaks more EXIF/GPS metadata than necessary.
 *
 * Pipeline per image:
 *  1. Read the `File` into an `<img>` via `URL.createObjectURL`.
 *  2. If max(width, height) > MAX_EDGE, scale to fit MAX_EDGE preserving aspect.
 *  3. Re-encode as JPEG quality 0.85 via `<canvas>.toDataURL`.
 *  4. If the resulting base64 raw bytes > MAX_BASE64_BYTES, throw
 *     `ImageTooLargeError` so the caller can show a "請使用較小的版本" toast.
 *
 * Sharp edges:
 *  - We never log the base64 string. Only file name (already user-visible) and
 *    byte counts. The base64 stays in memory inside the returned object.
 *  - The MIME type whitelist is enforced here too — anything that isn't
 *    `image/*` rejects with `NotAnImageError`. The picker `accept=` attribute
 *    is a hint, not a guarantee (Android browsers sometimes ignore it).
 *  - `URL.revokeObjectURL` is called even on the error path so we don't leak
 *    blob URLs.
 */

export const MAX_EDGE = 2048;
export const JPEG_QUALITY = 0.85;
/** 8 MB raw bytes — base64 expands by ~4/3, so the data URL itself can be larger. */
export const MAX_BASE64_BYTES = 8 * 1024 * 1024;

export class NotAnImageError extends Error {
  constructor(public readonly mime: string) {
    super(`Not an image MIME type: ${mime}`);
    this.name = 'NotAnImageError';
  }
}

export class ImageTooLargeError extends Error {
  constructor(public readonly bytes: number) {
    super(`Downscaled image still exceeds size limit: ${bytes} bytes`);
    this.name = 'ImageTooLargeError';
  }
}

export class ImageDecodeError extends Error {
  constructor(message = 'Failed to decode image') {
    super(message);
    this.name = 'ImageDecodeError';
  }
}

export interface DownscaledImage {
  /** Base64 (no data URL prefix). */
  data: string;
  /** Always `image/jpeg` after re-encode. */
  mime: 'image/jpeg';
  width: number;
  height: number;
  /** Approximate raw byte count of the base64 payload (not the data URL string). */
  bytes: number;
}

function isImageMime(mime: string): boolean {
  return typeof mime === 'string' && mime.toLowerCase().startsWith('image/');
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = (): void => resolve(img);
    img.onerror = (): void =>
      reject(new ImageDecodeError('Image element failed to load'));
    img.src = url;
  });
}

/**
 * Approximate the raw byte count encoded by a base64 string without
 * decoding it. Each 4 base64 chars → 3 bytes minus padding. We avoid
 * `atob` here because we don't need the bytes, only the size.
 */
export function base64ByteLength(b64: string): number {
  if (!b64) return 0;
  let padding = 0;
  if (b64.endsWith('==')) padding = 2;
  else if (b64.endsWith('=')) padding = 1;
  return Math.floor((b64.length * 3) / 4) - padding;
}

/**
 * Downscale + re-encode a single image. Throws on non-image MIME, decode
 * failure, or final-size overflow.
 */
export async function downscaleImage(file: File): Promise<DownscaledImage> {
  if (!isImageMime(file.type)) {
    throw new NotAnImageError(file.type || '(unknown)');
  }

  const url = URL.createObjectURL(file);
  let img: HTMLImageElement;
  try {
    img = await loadImage(url);
  } finally {
    URL.revokeObjectURL(url);
  }

  const w0 = img.naturalWidth || img.width;
  const h0 = img.naturalHeight || img.height;
  if (!w0 || !h0) throw new ImageDecodeError('Image has zero dimension');

  const longest = Math.max(w0, h0);
  const scale = longest > MAX_EDGE ? MAX_EDGE / longest : 1;
  const w = Math.max(1, Math.round(w0 * scale));
  const h = Math.max(1, Math.round(h0 * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new ImageDecodeError('Canvas 2D context unavailable');
  ctx.drawImage(img, 0, 0, w, h);

  const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  // dataUrl is `data:image/jpeg;base64,<...>`. Strip the prefix.
  const commaIdx = dataUrl.indexOf(',');
  const b64 = commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : '';
  const bytes = base64ByteLength(b64);

  if (bytes > MAX_BASE64_BYTES) {
    throw new ImageTooLargeError(bytes);
  }

  return {
    data: b64,
    mime: 'image/jpeg',
    width: w,
    height: h,
    bytes,
  };
}
